/**
 * IndexStaffingRow.tsx
 * Index Tests Phase 9, Item 9.4 (owner revision R2) — one pooled
 * IndexAllocation's staffing row in the People-phase "Index tests" section
 * (IndexStaffingSection.tsx): TaskType name / TestCount / window, currently-
 * assigned chips (with remove), and an add-person Autocomplete.
 *
 * Deliberately a LIGHTER picker than InlineAssigneePicker (tasks): an
 * IndexAllocation has no MachineBooking, so there is no per-machine scoping,
 * per-week cell targeting, or day-based availability projection to reuse (A4
 * — index allocations never touch the minutes/booking engine). What IS
 * reused, verbatim, from the task-assignment surfaces:
 *   - `listAssignableUsers` + `isQualifiedForTaskType` (scheduleQualification)
 *     — same qualification gate (BUG-2026-0094) so unrated users are hidden.
 *   - `AssigneeOptionRow` (skill level + tier chip; `showLoad=false` — no
 *     availability column, since there is no booking window to project onto).
 *   - `MentorRequiredModal` / `SkillGateError` — the SAME advisory mentor-gate
 *     UX as the task picker. The index endpoint assigns ONE user per call
 *     (indexAllocationStaffingController), so "Add Mentor" here sequences two
 *     calls: assign the mentor, then retry the originally-blocked user.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Cancel } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';

import type { IndexAllocation } from '../../../../../services/scheduleIndex';
import {
  assignToIndexAllocation,
  listIndexAllocationAssignees,
  unassignFromIndexAllocation,
  type IndexAllocationAssignee,
} from '../../../../../services/scheduleIndex';
import {
  listAssignableUsers,
  SkillGateError,
  type AssignableUser,
  type EligibleMentor,
  type SkillGateBlockedUser,
} from '../../../../../services/scheduleAssignments';
import { AssigneeOptionRow } from '../../../../../components/schedule/assigneeOption';
import { MentorRequiredModal } from '../../../../../components/schedule/MentorRequiredModal';
import { isQualifiedForTaskType } from '../../../../../utils/scheduleQualification';

export interface IndexStaffingRowProps {
  allocation: IndexAllocation;
  /** Submitted/Archived scenario — read-only (mirrors the rest of the People panel). */
  locked: boolean;
  /** Called after a successful assign/unassign so the parent can emit scheduleBus + refresh capacity. */
  onChanged: () => void;
}

export const IndexStaffingRow: React.FC<IndexStaffingRowProps> = ({
  allocation,
  locked,
  onChanged,
}) => {
  const theme = useTheme();
  const taskTypeKey = allocation.TaskTypeKey;

  const [assignees, setAssignees] = useState<IndexAllocationAssignee[]>([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);

  const loadAssignees = useCallback(async () => {
    setLoadingAssignees(true);
    try {
      const rows = await listIndexAllocationAssignees(allocation.IndexAllocationKey);
      setAssignees(rows);
    } catch {
      // Best-effort — chips stay at whatever was last loaded.
    } finally {
      setLoadingAssignees(false);
    }
  }, [allocation.IndexAllocationKey]);

  useEffect(() => {
    void loadAssignees();
  }, [loadAssignees]);

  const uniqueAssignees = useMemo(() => {
    const seen = new Set<string>();
    const out: IndexAllocationAssignee[] = [];
    for (const a of assignees) {
      if (seen.has(a.UserKey)) continue;
      seen.add(a.UserKey);
      out.push(a);
    }
    return out;
  }, [assignees]);
  const assignedKeys = useMemo(() => new Set(uniqueAssignees.map((a) => a.UserKey)), [uniqueAssignees]);

  // ── Assignable-user list (lazy, one fetch per open) ─────────────────────
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || users.length > 0) return;
    let cancelled = false;
    setLoadingUsers(true);
    listAssignableUsers(taskTypeKey)
      .then((list) => { if (!cancelled) setUsers(list); })
      .catch(() => { /* best-effort — dropdown just stays empty */ })
      .finally(() => { if (!cancelled) setLoadingUsers(false); });
    return () => { cancelled = true; };
  }, [open, users.length, taskTypeKey]);

  const pickableUsers = useMemo(
    () => users.filter((u) => !assignedKeys.has(u.AppUserKey) && isQualifiedForTaskType(u, taskTypeKey)),
    [users, assignedKeys, taskTypeKey],
  );

  // ── Assign / unassign / skill-gate resolution ────────────────────────────
  const [saving, setSaving] = useState(false);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingUserKey, setPendingUserKey] = useState<string | null>(null);
  const [mentorOpen, setMentorOpen] = useState(false);
  const [mentorBlocked, setMentorBlocked] = useState<SkillGateBlockedUser[]>([]);

  const doAssign = useCallback(
    async (userKey: string, confirmSkillGate = false): Promise<void> => {
      setSaving(true);
      setError(null);
      try {
        await assignToIndexAllocation(allocation.IndexAllocationKey, userKey, confirmSkillGate);
        await loadAssignees();
        onChanged();
      } catch (err) {
        if (err instanceof SkillGateError) {
          setPendingUserKey(userKey);
          setMentorBlocked(err.blockedUsers);
          setMentorOpen(true);
        } else {
          const axiosBody = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
          setError(axiosBody || (err instanceof Error ? err.message : 'Failed to assign.'));
        }
      } finally {
        setSaving(false);
      }
    },
    [allocation.IndexAllocationKey, loadAssignees, onChanged],
  );

  const handleSelect = useCallback(
    (user: AssignableUser | null) => {
      if (!user || saving) return;
      void doAssign(user.AppUserKey);
    },
    [doAssign, saving],
  );

  // The index assign endpoint takes ONE user per call (unlike the task bulk
  // endpoint), so "Add Mentor" assigns the mentor first, then retries the
  // originally-blocked user with confirmSkillGate — the mentor's presence is
  // exactly what the retry needs to clear the gate.
  const handleAddMentor = useCallback(
    (mentor: EligibleMentor) => {
      setMentorOpen(false);
      if (!pendingUserKey) return;
      void (async () => {
        setSaving(true);
        try {
          await assignToIndexAllocation(allocation.IndexAllocationKey, mentor.AppUserKey);
        } catch {
          // The mentor may already be assigned, or another advisory blip —
          // proceed to the retry regardless; a real failure surfaces there.
        }
        await doAssign(pendingUserKey, true);
      })();
    },
    [pendingUserKey, allocation.IndexAllocationKey, doAssign],
  );

  const handleAssignAnyway = useCallback(() => {
    setMentorOpen(false);
    if (pendingUserKey) void doAssign(pendingUserKey, true);
  }, [pendingUserKey, doAssign]);

  const handleUnassign = useCallback(
    (userKey: string) => {
      if (removingKey) return;
      setRemovingKey(userKey);
      setError(null);
      void unassignFromIndexAllocation(allocation.IndexAllocationKey, userKey)
        .then(async () => {
          await loadAssignees();
          onChanged();
        })
        .catch((err: unknown) => {
          const axiosBody = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
          setError(axiosBody || (err instanceof Error ? err.message : 'Failed to remove.'));
        })
        .finally(() => setRemovingKey(null));
    },
    [removingKey, allocation.IndexAllocationKey, loadAssignees, onChanged],
  );

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: `${theme.border.radius}px`,
        border: `1px solid ${theme.border.color}`,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>
          {allocation.TaskTypeName ?? 'Index test'}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
          {allocation.TestCount} tests · {allocation.StartDate} → {allocation.EndDate}
        </Typography>
        {loadingAssignees && <CircularProgress size={12} />}
      </Box>

      {/* Currently-staffed chips */}
      {uniqueAssignees.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
          {uniqueAssignees.map((a) => {
            const color = a.Status === 'Committed' ? theme.schedule.committed : theme.schedule.employeeAlloc;
            const isRemoving = removingKey === a.UserKey;
            return (
              <Tooltip
                key={a.UserKey}
                arrow
                title={isRemoving ? 'Removing…' : `${a.displayName}${a.tier ? ` · ${a.tier}` : ''} — click × to remove`}
              >
                <Chip
                  label={a.displayName}
                  size="small"
                  onDelete={locked || removingKey ? undefined : () => handleUnassign(a.UserKey)}
                  deleteIcon={isRemoving ? <CircularProgress size={11} sx={{ color }} /> : <Cancel aria-label={`Remove ${a.displayName}`} />}
                  sx={{
                    fontSize: '0.68rem',
                    height: 20,
                    backgroundColor: alpha(color, 0.12),
                    color,
                    fontWeight: 600,
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      )}

      {/* Add-person control */}
      {!locked && (
        <Autocomplete<AssignableUser, false, false, false>
          size="small"
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          options={pickableUsers}
          getOptionLabel={(u) => u.DisplayName}
          isOptionEqualToValue={(a, b) => a.AppUserKey === b.AppUserKey}
          loading={loadingUsers}
          disabled={saving}
          value={null}
          onChange={(_e, value) => handleSelect(value)}
          blurOnSelect
          clearOnBlur
          clearOnEscape
          noOptionsText={loadingUsers ? 'Loading…' : 'No qualified users to add'}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={uniqueAssignees.length > 0 ? 'Add another…' : 'Assign…'}
              size="small"
              inputProps={{ ...params.inputProps, style: { fontSize: '0.78rem', padding: '4px 6px' } }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {(loadingUsers || saving) && <CircularProgress size={14} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { paddingY: 0.25, fontSize: '0.78rem' }, maxWidth: 320 }}
            />
          )}
          componentsProps={{ paper: { sx: { minWidth: 320 } } }}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ py: 0.6 }}>
              <AssigneeOptionRow
                displayName={option.DisplayName}
                tier={option.Tier}
                skillLevel={option.SkillLevel}
                showSkill={Boolean(taskTypeKey)}
                load={null}
                showLoad={false}
              />
            </Box>
          )}
        />
      )}

      {error && (
        <Typography variant="caption" sx={{ color: theme.schedule.conflictHard, fontSize: '0.68rem' }}>
          {error}
        </Typography>
      )}

      <MentorRequiredModal
        open={mentorOpen}
        taskTypeKey={taskTypeKey}
        blockedUsers={mentorBlocked}
        onAddMentor={handleAddMentor}
        onAssignAnyway={handleAssignAnyway}
        onClose={() => setMentorOpen(false)}
      />
    </Box>
  );
};
